import React, { useEffect, useMemo, useRef } from "react";
import ReactDom from "react-dom";
import Styles from "./TransitionModal.module.scss";
import clsx from "clsx";
import { CloseSvg } from "@/assets/icons";
import { useCssTransition } from "@/hooks/useCssTransition";

interface IProps {
  visible: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  getContainer?: () => HTMLElement;
  mask?: boolean;
  timeout?: number;
  dragable?: boolean;
  title?: string | React.ReactNode;
  initHeight?: number | string;
  wrapperClassName?: string;
  className?: string;
  contentClassName?: string;
  dragClassName?: string;
  idClassName?: string;
  fitContent?: boolean;

  /** @name 动画完成渲染组件? */
  enteredRender?: boolean;

  titleRender?: React.ReactNode;
}
const firstHeight = 80;
const TransitionModal = React.forwardRef((props: IProps, ref) => {
  const {
    enteredRender = true,
    fitContent = false,
    idClassName = "main",
    className,
    wrapperClassName,
    contentClassName,
    visible = false,
    mask = true,
    timeout = 500,
    dragable = false,
    initHeight = 70,
    title,
    titleRender,
  } = props;
  const [hide, setHide] = React.useState(true);
  const [childCanRender, setChildCanRender] = React.useState(!enteredRender);
  const nodeRef = useRef<HTMLDivElement>(null);
  const conetentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState<string | number>(50);
  const wrapperHeightRef = useRef(0);
  const draggingRef = useRef(false);
  const dragElementRef = useRef<HTMLDivElement>(null);
  const { enter, exit } = useCssTransition(nodeRef);

  useEffect(() => {
    if (visible) {
      setHide(false);
      setContentHeight(initHeight ?? 50);
      setChildCanRender(true);
      enter()
    } else {
      
      exit().then(()=>{
        setHide(true);
      });
    }
  }, [visible, initHeight]);
  const container = useMemo(() => {
    if (props.getContainer) {
      return props.getContainer();
    }
    return document.body;
  }, [props.getContainer]);

  React.useImperativeHandle(ref, () => ({
    close: () => {
      props.onClose?.();
    },
  }));

  const handleClose = () => {
    exit().then(()=>{
      setHide(true);
      props.onClose?.();
    });
  };

  const handleTouchStart = (e: any) => {
    const wrapperHeight = nodeRef.current?.clientHeight ?? 0;
    wrapperHeightRef.current = wrapperHeight;
    const clientY = e.touches[0].clientY;
    const startY = clientY;
    const contentStartHeight = conetentRef.current?.clientHeight ?? 0;
    draggingRef.current = true;
    let nowContentHeight = 0;
    const dragElmentHeight = dragElementRef.current?.clientHeight ?? 0;
    const maxHeight = wrapperHeight - 60 - dragElmentHeight;
    const maxHeightPercent = (maxHeight / wrapperHeight) * 100;
    const handleTouchMove = (e: any) => {
      const clientY = e.touches[0].clientY;
      const moveY = clientY - startY;
      const nowHeight = contentStartHeight - moveY;
      const percentHeight = (nowHeight / wrapperHeight) * 100;

      if (percentHeight > 60) {
        nowContentHeight = 70;
      } else if (percentHeight <= 60 && percentHeight >= 30) {
        nowContentHeight = 50;
      } else {
        nowContentHeight = Math.min(percentHeight, 70);
      }
      nowContentHeight = percentHeight;
      setContentHeight(percentHeight);
    };

    const handleTouchEnd = () => {
      draggingRef.current = true;

      if (nowContentHeight > firstHeight - 10) {
        nowContentHeight = firstHeight;
      } else if (
        nowContentHeight <= firstHeight - 10 &&
        nowContentHeight >= 30
      ) {
        nowContentHeight = 50;
      } else {
        nowContentHeight = Math.min(nowContentHeight, firstHeight);
      }
      setContentHeight(nowContentHeight);

      if (typeof nowContentHeight === "number" && nowContentHeight < 30) {
        setTimeout(() => {
          setHide(true);
        }, timeout);
        props.onClose?.();
      }
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };
  return ReactDom.createPortal(
    <div
      className={clsx(
        Styles.wrapper_wrap,
        !hide && Styles.wrapper_wrap_visible,
        wrapperClassName
      )}
    >
      {mask && (
        <div
          hidden={hide}
          className={Styles.mask}
          onClick={handleClose}
        ></div>
      )}
      <div
        className={clsx(
          Styles.wrapper,
          className,
          fitContent && Styles.fitContent
        )}
        ref={nodeRef}
        data-visible={visible}
        onClick={handleClose}
      >
        <div
          className={clsx(Styles.content, contentClassName)}
          style={{
            height:
              typeof contentHeight === "number"
                ? `${contentHeight}%`
                : contentHeight,
          }}
          ref={conetentRef}
          onClick={(e) => e.stopPropagation()}
        >
          {title && !titleRender && (
            <div className={Styles.title}>
              <div className={Styles.close} onClick={handleClose}>
                <CloseSvg />
              </div>
              <div className={Styles.title_text}>{title}</div>
            </div>
          )}
          {titleRender}
          {dragable && (
            <div
              ref={dragElementRef}
              className={clsx(Styles.slidesControll)}
              onTouchStart={handleTouchStart}
            >
              <div className={Styles.slidesControllContent}></div>
            </div>
          )}
          <div className={Styles.container}>
            {childCanRender && props.children}
          </div>
        </div>
      </div>
    </div>,
    container
  );
});

TransitionModal.displayName = "TransitionModal";

export default TransitionModal;
